import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedLines } from "@/lib/motion";
import { CosmicField } from "./CosmicField";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const yContent = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative isolate min-h-[100svh] flex flex-col justify-center overflow-hidden"
    >
      {/* Immersive cosmic environment (Option 1) */}
      <motion.div style={{ y: yImg, scale: scaleImg }} className="absolute inset-0 -z-10">
        <CosmicField />
      </motion.div>

      <motion.div style={{ y: yContent, opacity }} className="mx-auto w-full max-w-7xl px-5 sm:px-8 pt-28">
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05 }}
          data-testid="prelaunch-notice"
          className="mb-8 max-w-3xl rounded-2xl border border-cyan-300/20 bg-white/[0.03] px-5 py-4 sm:px-6 backdrop-blur-md"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
            <div className="flex-1">
              <p className="text-sm font-500 text-white">ArcaCentum is preparing for launch.</p>
              <p className="mt-1 text-xs sm:text-[13px] leading-relaxed text-white/55 font-light">
                We're building a new ecosystem of AI-powered software and business technology.
                Our platforms are currently in development and are not yet available for public use.
              </p>
            </div>
            <span className="inline-flex w-fit shrink-0 items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-400/10 px-3.5 py-1.5 text-[11px] uppercase tracking-[0.18em] text-cyan-200">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-75" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-300" />
              </span>
              Coming Soon
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 backdrop-blur-sm"
          data-testid="hero-badge"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-cyan-300" />
          </span>
          <span className="text-xs uppercase tracking-[0.3em] text-white/70">Ideas Into Reality</span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-display text-4xl sm:text-5xl lg:text-[4.6rem] font-600 leading-[1.03] tracking-[-0.02em] max-w-5xl"
        >
          <MaskedLines
            lines={["Intelligence", "for a more"]}
            lineClassName="text-white"
            delay={0.35}
          />
          <MaskedLines
            lines={["human future."]}
            lineClassName="text-gradient"
            delay={0.6}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          data-testid="hero-subheadline"
          className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-white/80 font-light"
        >
          ArcaCentum is an AI technology company creating intelligent products that
          help people turn ideas into real-world software, businesses, and opportunities.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0 }}
          className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3"
        >
          <a
            href="#products"
            data-testid="hero-cta-primary"
            className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-full bg-white px-7 py-3.5 text-sm font-500 text-[#0a0a0f] transition-transform duration-300 hover:scale-[1.02]"
          >
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-violet-300 to-cyan-200 transition-transform duration-500 group-hover:translate-x-0" />
            <span className="relative z-10">Explore Our Products</span>
            <ArrowDown size={16} className="relative z-10 transition-transform duration-300 group-hover:translate-y-0.5" />
          </a>
          <a
            href="#about"
            data-testid="hero-cta-secondary"
            className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.02] px-7 py-3.5 text-sm text-white/85 backdrop-blur-sm transition-all duration-300 hover:border-white/30 hover:bg-white/[0.05]"
          >
            About ArcaCentum
            <ArrowUpRight size={16} className="text-violet-300 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          data-testid="hero-microcopy"
          className="mt-9 text-[10px] uppercase tracking-[0.32em] text-white/35"
        >
          People <span className="text-white/20 mx-1.5">/</span> Ideas <span className="text-white/20 mx-1.5">/</span> Technology <span className="text-white/20 mx-1.5">/</span> Opportunity
        </motion.p>
      </motion.div>

      {/* Ecosystem preview strip — real products, pre-launch state preserved */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.35 }}
        data-testid="hero-ecosystem-strip"
        className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 mt-14 lg:mt-20 pb-20"
      >
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { name: "ArcaCentum.ai", sub: "AI App Studio", line: "Describe. Build. Launch.", dev: true, href: "#products", testid: "strip-ai" },
            { name: "ArcaCapitalis", sub: "Business Building Platform", line: "From concept to company.", dev: true, href: "#products", testid: "strip-capitalis" },
            { name: "ArcaCentum Credit", sub: "Business & Personal Credit", line: "Build. Access. Grow.", dev: true, href: "#products", testid: "strip-credit" },
            { name: "More to Come", sub: "Expanding the Ecosystem", line: "New products on the horizon.", dev: false, href: "#ecosystem", testid: "strip-more" },
          ].map((c) => (
            <a
              key={c.name}
              href={c.href}
              data-testid={c.testid}
              className="group rounded-2xl border border-white/10 bg-[#0b0b14]/55 px-5 py-4 backdrop-blur-md transition-colors duration-300 hover:border-cyan-300/30 hover:bg-[#0b0b14]/75"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-500 text-white">{c.name}</p>
                  <p className="mt-0.5 text-[11px] text-cyan-200/70">{c.sub}</p>
                </div>
                <ArrowUpRight size={15} className="shrink-0 text-white/35 transition-all duration-300 group-hover:text-cyan-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
              <p className="mt-2.5 text-xs text-white/45 font-light">{c.line}</p>
              {c.dev && (
                <p className="mt-2 inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.18em] text-amber-200/80">
                  <span className="h-1 w-1 rounded-full bg-amber-300" /> In Development · Coming Soon
                </p>
              )}
            </a>
          ))}
        </div>
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/30"
      >
        <span className="text-[10px] uppercase tracking-[0.25em]">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={16} />
        </motion.div>
      </motion.div>
    </section>
  );
};
